import { v4 as uuidv4 } from 'uuid';
import { MaintenanceReport, MaintenanceProblemType } from '@/models/maintenance';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Screen } from '@/models/screen';
import { UserWithProfile } from '@/models/user';

const MAINTENANCE_REPORTS_KEY = 'maintenance_reports';
const COMPLETED_MAINTENANCE_REPORTS_KEY = 'completed_maintenance_reports';
const DISMISSED_NOTIFICATIONS_KEY = 'dismissed_maintenance_notifications';

// Función para obtener todos los reportes de mantenimiento pendientes
export const getPendingMaintenanceReports = async (): Promise<MaintenanceReport[]> => {
  try {
    const { data, error } = await supabase
      .from('maintenance_reports')
      .select(`
        id, 
        screen_id, 
        screen_name, 
        screen_location, 
        problems, 
        estimated_time, 
        details, 
        reported_by, 
        reported_by_name, 
        reported_at, 
        completed_at, 
        status,
        is_dismissed
      `)
      .eq('status', 'pending');

    if (error) {
      console.error('Error al obtener reportes de mantenimiento:', error);
      return [];
    }

    // Convertir los datos de Supabase al formato de nuestro modelo
    return data.map(item => ({
      id: item.id,
      screenId: item.screen_id,
      problems: item.problems as MaintenanceProblemType[],
      estimatedTimeHours: item.estimated_time,
      details: item.details || '',
      createdAt: item.reported_at,
      reportedById: item.reported_by,
      completedAt: item.completed_at,
      isCompleted: item.status === 'completed',
      isDismissed: item.is_dismissed || false,
      // Información adicional que podría ser útil
      screen: {
        id: item.screen_id,
        name: item.screen_name,
        location: item.screen_location
      } as Screen,
      reportedBy: {
        id: item.reported_by,
        profile: {
          full_name: item.reported_by_name
        }
      } as UserWithProfile
    }));
  } catch (error) {
    console.error('Error al obtener reportes de mantenimiento:', error);
    return [];
  }
};

// Función para obtener todos los reportes de mantenimiento completados
export const getCompletedMaintenanceReports = async (): Promise<MaintenanceReport[]> => {
  try {
    const { data, error } = await supabase
      .from('maintenance_reports')
      .select(`
        id, 
        screen_id, 
        screen_name, 
        screen_location, 
        problems, 
        estimated_time, 
        details, 
        reported_by, 
        reported_by_name, 
        reported_at, 
        completed_at, 
        status,
        is_dismissed
      `)
      .eq('status', 'completed')
      // No filtramos por is_dismissed para mostrar todas en el historial
      .order('completed_at', { ascending: false });

    if (error) {
      console.error('Error al obtener reportes de mantenimiento completados:', error);
      return [];
    }

    // Convertir los datos de Supabase al formato de nuestro modelo
    return data.map(item => ({
      id: item.id,
      screenId: item.screen_id,
      problems: item.problems as MaintenanceProblemType[],
      estimatedTimeHours: item.estimated_time,
      details: item.details || '',
      createdAt: item.reported_at,
      reportedById: item.reported_by,
      completedAt: item.completed_at,
      isCompleted: true,
      isDismissed: item.is_dismissed || false,
      // Información adicional que podría ser útil
      screen: {
        id: item.screen_id,
        name: item.screen_name,
        location: item.screen_location
      } as Screen,
      reportedBy: {
        id: item.reported_by,
        profile: {
          full_name: item.reported_by_name
        }
      } as UserWithProfile
    }));
  } catch (error) {
    console.error('Error al obtener reportes de mantenimiento completados:', error);
    return [];
  }
};

// Función para crear un nuevo reporte de mantenimiento
export const createMaintenanceReport = async (
  reportData: Omit<MaintenanceReport, 'id' | 'createdAt' | 'isCompleted'>,
  screenName: string,
  screenLocation: string,
  reportedByName: string
): Promise<MaintenanceReport | null> => {
  try {
    const { data, error } = await supabase
      .from('maintenance_reports')
      .insert({
        screen_id: reportData.screenId,
        screen_name: screenName,
        screen_location: screenLocation,
        problems: reportData.problems,
        estimated_time: reportData.estimatedTimeHours,
        details: reportData.details,
        reported_by: reportData.reportedById,
        reported_by_name: reportedByName,
        status: 'pending'
      })
      .select('*')
      .single();

    if (error) {
      console.error('Error al crear reporte de mantenimiento:', error);
      return null;
    }

    // Convertir el resultado a nuestro formato de modelo
    return {
      id: data.id,
      screenId: data.screen_id,
      problems: data.problems as MaintenanceProblemType[],
      estimatedTimeHours: data.estimated_time,
      details: data.details || '',
      createdAt: data.reported_at,
      reportedById: data.reported_by,
      completedAt: null,
      isCompleted: false,
      screen: {
        id: data.screen_id,
        name: data.screen_name,
        location: data.screen_location
      } as Screen,
      reportedBy: {
        id: data.reported_by,
        profile: {
          full_name: data.reported_by_name
        }
      } as UserWithProfile
    };
  } catch (error) {
    console.error('Error al crear reporte de mantenimiento:', error);
    return null;
  }
};

// Función para marcar un reporte como completado
export const completeMaintenanceReport = async (reportId: string): Promise<MaintenanceReport | null> => {
  try {
    const now = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('maintenance_reports')
      .update({
        completed_at: now,
        status: 'completed'
      })
      .eq('id', reportId)
      .select('*')
      .single();

    if (error) {
      console.error('Error al completar reporte de mantenimiento:', error);
      return null;
    }

    // Convertir el resultado a nuestro formato de modelo
    return {
      id: data.id,
      screenId: data.screen_id,
      problems: data.problems as MaintenanceProblemType[],
      estimatedTimeHours: data.estimated_time,
      details: data.details || '',
      createdAt: data.reported_at,
      reportedById: data.reported_by,
      completedAt: data.completed_at,
      isCompleted: true,
      screen: {
        id: data.screen_id,
        name: data.screen_name,
        location: data.screen_location
      } as Screen,
      reportedBy: {
        id: data.reported_by,
        profile: {
          full_name: data.reported_by_name
        }
      } as UserWithProfile
    };
  } catch (error) {
    console.error('Error al completar reporte de mantenimiento:', error);
    return null;
  }
};

// Función para eliminar un reporte de mantenimiento completado
export const deleteCompletedMaintenanceReport = async (reportId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('maintenance_reports')
      .delete()
      .eq('id', reportId)
      .eq('status', 'completed');

    if (error) {
      console.error('Error al eliminar reporte de mantenimiento:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error al eliminar reporte de mantenimiento completado:', error);
    return false;
  }
};

// Función para obtener un reporte específico
export const getMaintenanceReportById = async (reportId: string): Promise<MaintenanceReport | null> => {
  try {
    const { data, error } = await supabase
      .from('maintenance_reports')
      .select(`
        id, 
        screen_id, 
        screen_name, 
        screen_location, 
        problems, 
        estimated_time, 
        details, 
        reported_by, 
        reported_by_name, 
        reported_at, 
        completed_at, 
        status
      `)
      .eq('id', reportId)
      .single();

    if (error || !data) {
      console.error('Error al obtener reporte de mantenimiento:', error);
      return null;
    }

    // Convertir el resultado a nuestro formato de modelo
    return {
      id: data.id,
      screenId: data.screen_id,
      problems: data.problems as MaintenanceProblemType[],
      estimatedTimeHours: data.estimated_time,
      details: data.details || '',
      createdAt: data.reported_at,
      reportedById: data.reported_by,
      completedAt: data.completed_at,
      isCompleted: data.status === 'completed',
      screen: {
        id: data.screen_id,
        name: data.screen_name,
        location: data.screen_location
      } as Screen,
      reportedBy: {
        id: data.reported_by,
        profile: {
          full_name: data.reported_by_name
        }
      } as UserWithProfile
    };
  } catch (error) {
    console.error('Error al obtener reporte de mantenimiento por ID:', error);
    return null;
  }
};

// Función para migrar datos de localStorage a Supabase (uso único)
export const migrateMaintenanceDataToSupabase = async (): Promise<boolean> => {
  try {
    // Obtener datos de localStorage
    const pendingReportsJson = localStorage.getItem('maintenance_reports');
    const completedReportsJson = localStorage.getItem('completed_maintenance_reports');
    
    const pendingReports = pendingReportsJson ? JSON.parse(pendingReportsJson) : [];
    const completedReports = completedReportsJson ? JSON.parse(completedReportsJson) : [];
    
    // Combinar todos los reportes
    const allReports = [
      ...pendingReports.map((report: any) => ({ ...report, status: 'pending' })),
      ...completedReports.map((report: any) => ({ ...report, status: 'completed' }))
    ];
    
    if (allReports.length === 0) {
      return true; // No hay datos para migrar
    }
    
    // Preparar datos para inserción en Supabase
    const reportsToInsert = allReports.map((report: any) => ({
      id: report.id,
      screen_id: report.screenId,
      screen_name: report.screen?.name || 'Pantalla sin nombre',
      screen_location: report.screen?.location || 'Ubicación desconocida',
      problems: report.problems,
      estimated_time: report.estimatedTimeHours,
      details: report.details || '',
      reported_by: report.reportedById,
      reported_by_name: report.reportedBy?.profile?.full_name || 'Usuario desconocido',
      reported_at: report.createdAt,
      completed_at: report.completedAt || null,
      status: report.status
    }));
    
    // Insertar en Supabase
    const { error } = await supabase
      .from('maintenance_reports')
      .upsert(reportsToInsert, { onConflict: 'id' });
    
    if (error) {
      console.error('Error al migrar datos a Supabase:', error);
      return false;
    }
    
    // Limpiar localStorage después de migrar exitosamente
    localStorage.removeItem('maintenance_reports');
    localStorage.removeItem('completed_maintenance_reports');
    
    return true;
  } catch (error) {
    console.error('Error durante la migración de datos:', error);
    return false;
  }
};

// Función para contar pantallas en mantenimiento
export const countScreensInMaintenance = async (): Promise<number> => {
  try {
    const { count, error } = await supabase
      .from('maintenance_reports')
      .select('id', { count: 'exact' })
      .eq('status', 'pending');
    
    if (error) {
      console.error('Error al contar pantallas en mantenimiento:', error);
      return 0;
    }
    
    return count || 0;
  } catch (error) {
    console.error('Error al contar pantallas en mantenimiento:', error);
    return 0;
  }
};

// Función para obtener reportes de mantenimiento de una pantalla específica
export const getMaintenanceReportsByScreenId = async (screenId: string): Promise<MaintenanceReport[]> => {
  try {
    const { data, error } = await supabase
      .from('maintenance_reports')
      .select(`
        id, 
        screen_id, 
        screen_name, 
        screen_location, 
        problems, 
        estimated_time, 
        details, 
        reported_by, 
        reported_by_name, 
        reported_at, 
        completed_at, 
        status
      `)
      .eq('screen_id', screenId)
      .order('reported_at', { ascending: false });

    if (error) {
      console.error('Error al obtener reportes de mantenimiento por pantalla:', error);
      return [];
    }

    // Convertir los datos de Supabase al formato de nuestro modelo
    return data.map(item => ({
      id: item.id,
      screenId: item.screen_id,
      problems: item.problems as MaintenanceProblemType[],
      estimatedTimeHours: item.estimated_time,
      details: item.details || '',
      createdAt: item.reported_at,
      reportedById: item.reported_by,
      completedAt: item.completed_at,
      isCompleted: item.status === 'completed',
      screen: {
        id: item.screen_id,
        name: item.screen_name,
        location: item.screen_location
      } as Screen,
      reportedBy: {
        id: item.reported_by,
        profile: {
          full_name: item.reported_by_name
        }
      } as UserWithProfile
    }));
  } catch (error) {
    console.error('Error al obtener reportes de mantenimiento por pantalla:', error);
    return [];
  }
};

// Función para marcar permanentemente un reporte de mantenimiento como descartado
export const markMaintenanceReportAsDismissed = async (reportId: string): Promise<boolean> => {
  try {
    // Actualizar el reporte en Supabase con un campo adicional 'is_dismissed'
    const { error } = await supabase
      .from('maintenance_reports')
      .update({ is_dismissed: true })
      .eq('id', reportId);

    if (error) {
      console.error('Error al marcar reporte como descartado:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error al marcar reporte como descartado:', error);
    return false;
  }
}; 